import { HomeFooter } from "@/components/boardblend/home-footer";
import { LegalHeader } from "@/components/boardblend/legal-header";
import { PublicPageHead } from "@/components/seo/public-page-head";
import { staticPublicPages } from "@/lib/public-seo";

export default function TermsPage() {
  return (
    <>
      <PublicPageHead page={staticPublicPages.terms} />
      <main className="legal-page">
        <LegalHeader />
        <article>
          <p className="eyebrow">Terms of Use</p>
          <h1>Terms of Use</h1>
          <p>
            <strong>Effective date: July 23, 2026</strong>
          </p>
          <p>
            These Terms of Use govern your access to and use of Boardblend. By
            creating an account, joining a session, or otherwise using
            Boardblend, you agree to these Terms and our Privacy Policy.
          </p>

          <h2>1. Using Boardblend</h2>
          <p>
            Boardblend is a collaborative workspace where teams can discuss
            ideas, use AI assistants, upload context, and build a shared visual
            canvas.
          </p>
          <p>You must:</p>
          <ul>
            <li>
              Be at least 18 years old, or the age required to enter a binding
              agreement where you live.
            </li>
            <li>Provide accurate account information.</li>
            <li>Keep your login credentials secure.</li>
            <li>
              Use Boardblend only in accordance with applicable laws and these
              Terms.
            </li>
          </ul>
          <p>
            When using Boardblend on behalf of a company or organization, you
            confirm that you have authority to accept these Terms on its behalf.
          </p>

          <h2>2. Accounts</h2>
          <p>You are responsible for activity performed through your account.</p>
          <p>
            We may require email verification or other reasonable steps to
            protect accounts and sessions.
          </p>

          <h2>3. Sessions and participants</h2>
          <p>
            A session owner may invite other people through a room code or other
            available method.
          </p>
          <p>Session owners are responsible for:</p>
          <ul>
            <li>Deciding who may join their sessions.</li>
            <li>Protecting room codes.</li>
            <li>Managing the content and participants in their sessions.</li>
            <li>
              Ensuring participants understand when transcription or other
              recording features are enabled.
            </li>
          </ul>
          <p>
            Participants should only access sessions they have permission to
            join. Content shared in a session may be visible to its owner and
            other authorized participants. Session owners may be able to edit,
            retain, export, or delete shared session content.
          </p>

          <h2>4. Free and paid plans</h2>
          <p>
            Boardblend may offer Free, Pro, trial, promotional, or other plans.
          </p>
          <p>Under the current Free plan, users may:</p>
          <ul>
            <li>Join sessions owned by other people.</li>
            <li>Own one active brainstorm at a time.</li>
            <li>Delete or repurpose that brainstorm to begin another.</li>
            <li>Use the core features available under the Free plan.</li>
          </ul>
          <p>
            Pro users may create and retain additional brainstorms and access
            other paid features described in the pricing section.
          </p>
          <p>
            We may change plan limits or features over time. Changes will not
            reduce a prepaid subscription during its current billing period
            unless reasonably necessary for security, legal, or technical
            reasons.
          </p>

          <h2>5. Subscriptions and payment</h2>
          <p>
            Paid subscriptions are billed monthly in advance.
          </p>
          <p>
            By purchasing a subscription, you authorize Boardblend and its
            payment provider to charge:
          </p>
          <ul>
            <li>The displayed subscription price.</li>
            <li>Applicable taxes.</li>
            <li>
              Any other charges clearly disclosed and accepted before purchase.
            </li>
          </ul>
          <p>Subscriptions automatically renew until cancelled.</p>
          <p>
            You may cancel through your account settings. Cancellation takes
            effect at the end of the current paid billing period, and you will
            retain paid access until then.
          </p>
          <p>
            Except where required by law or expressly stated at purchase,
            payments are non-refundable and we do not provide credits for
            partially used billing periods.
          </p>
          <p>
            We may change subscription prices by providing reasonable advance
            notice. A price change will apply to a later renewal, not a billing
            period already paid for.
          </p>

          <h2>6. What happens after cancellation</h2>
          <p>When a Pro subscription ends:</p>
          <ul>
            <li>
              Your existing brainstorms remain accessible, subject to these
              Terms and the available Free-plan functionality.
            </li>
            <li>
              Your content and session history are not automatically deleted
              solely because you cancelled.
            </li>
            <li>
              You may be unable to create another owned brainstorm while you
              exceed the Free plan&apos;s current limit.
            </li>
            <li>
              You may delete or repurpose an existing brainstorm, or subscribe
              again.
            </li>
          </ul>

          <h2>7. Your content</h2>
          <p>
            You retain ownership of content you create or upload to Boardblend,
            including documents, comments, transcripts, contributions, and
            canvas content.
          </p>
          <p>
            You give Boardblend a worldwide, non-exclusive, royalty-free licence
            to host, process, reproduce, display, transmit, and modify your
            content only as reasonably necessary to:
          </p>
          <ul>
            <li>Provide and operate Boardblend.</li>
            <li>Synchronize sessions and collaboration.</li>
            <li>Support features you request.</li>
            <li>Maintain security and prevent abuse.</li>
            <li>Comply with legal obligations.</li>
          </ul>
          <p>
            This licence ends when the content is deleted from our systems,
            subject to reasonable backup retention and legal requirements.
          </p>
          <p>
            You confirm that you have the rights and permissions required to
            upload and share your content.
          </p>

          <h2>8. Shared contributions</h2>
          <p>
            When you submit a comment, canvas update, document, or other
            contribution to a shared session, you authorize the session owner
            and other permitted participants to view and use it as part of that
            session.
          </p>
          <p>
            The session owner may accept, reject, edit, organize, retain,
            export, or delete contributions according to Boardblend&apos;s
            available controls.
          </p>
          <p>
            Do not submit confidential, proprietary, or personal information
            unless you are authorized to share it with everyone who can access
            the session.
          </p>

          <h2>9. Transcription and meeting audio</h2>
          <p>
            Boardblend may allow a session owner to process microphone or
            browser-tab audio to produce a live transcript. Transcripts are
            automated and may be incomplete or inaccurate.
          </p>
          <p>The person enabling transcription is responsible for:</p>
          <ul>
            <li>
              Clearly informing everyone whose speech may be processed.
            </li>
            <li>Obtaining any consent required by applicable law.</li>
            <li>Using transcription only where permitted.</li>
            <li>
              Avoiding the transcription of people who have not agreed to
              participate.
            </li>
          </ul>
          <p>
            You must not secretly record or transcribe conversations through
            Boardblend.
          </p>

          <h2>10. AI assistants and third-party services</h2>
          <p>
            Boardblend may allow participants to connect third-party AI
            assistants or other external services.
          </p>
          <p>When you use a connected service:</p>
          <ul>
            <li>Information may be sent to that provider at your direction.</li>
            <li>The provider&apos;s own terms and privacy policy apply.</li>
            <li>
              Boardblend does not control the provider&apos;s availability,
              security, outputs, or data practices.
            </li>
            <li>
              You are responsible for ensuring you are permitted to share
              session content with that provider.
            </li>
          </ul>
          <p>
            AI-generated responses and suggested canvas changes may be
            incorrect, incomplete, biased, or unsuitable for your needs.
          </p>
          <p>
            You are responsible for reviewing AI output before relying on it or
            sharing it with others. Boardblend does not provide legal, medical,
            financial, engineering, or other professional advice.
          </p>

          <h2>11. Acceptable use</h2>
          <p>You may not use Boardblend to:</p>
          <ul>
            <li>Break the law or violate another person&apos;s rights.</li>
            <li>Upload content you do not have permission to use.</li>
            <li>Harass, threaten, defame, exploit, or impersonate others.</li>
            <li>Distribute malware or harmful code.</li>
            <li>
              Gain unauthorized access to accounts, sessions, systems, or data.
            </li>
            <li>
              Circumvent plan limits, security measures, or usage restrictions.
            </li>
            <li>Scrape, probe, overload, or disrupt the service.</li>
            <li>
              Use Boardblend to develop or operate harmful, fraudulent, or
              deceptive systems.
            </li>
            <li>
              Share highly sensitive information where doing so would be
              inappropriate or unlawful.
            </li>
            <li>
              Reverse engineer the service except where applicable law expressly
              permits it.
            </li>
          </ul>
          <p>
            We may investigate suspected violations and remove content, restrict
            access, or suspend accounts where reasonably necessary.
          </p>

          <h2>12. Intellectual property</h2>
          <p>
            Boardblend and its licensors own the service, including its
            software, design, branding, interfaces, templates, and other
            materials, excluding user content.
          </p>
          <p>
            These Terms give you a limited, revocable, non-exclusive,
            non-transferable right to use Boardblend for its intended purpose.
            They do not transfer ownership of Boardblend or its intellectual
            property to you.
          </p>
          <p>
            “Boardblend,” its logos, and related branding may not be used
            without our written permission.
          </p>

          <h2>13. Feedback</h2>
          <p>
            You may send us suggestions, ideas, or feedback. You grant us
            permission to use feedback without restriction or compensation,
            provided we do not publicly identify you as its source without
            permission.
          </p>

          <h2>14. Copyright complaints</h2>
          <p>A copyright complaint should include:</p>
          <ul>
            <li>Your contact information.</li>
            <li>Identification of the protected work.</li>
            <li>Identification of the allegedly infringing content.</li>
            <li>The session or location where it appears.</li>
            <li>
              A statement explaining why you believe the use is unauthorized.
            </li>
            <li>
              Any other information reasonably needed to evaluate the
              complaint.
            </li>
          </ul>
          <p>
            We may remove or restrict access to disputed content while reviewing
            a complaint.
          </p>

          <h2>15. Service changes and availability</h2>
          <p>
            Boardblend may evolve over time. We may add, change, suspend, or
            discontinue features.
          </p>
          <p>
            We do not guarantee that Boardblend will always be available,
            uninterrupted, secure, or error-free. Maintenance, technical
            failures, third-party outages, or security concerns may temporarily
            affect availability.
          </p>
          <p>
            Where reasonably possible, we will provide notice before
            discontinuing a paid service or making a material change that
            significantly affects paid users.
          </p>

          <h2>16. Beta features</h2>
          <p>
            Some features may be labelled beta, preview, experimental, or early
            access. These features may be incomplete, change without notice,
            perform unexpectedly, or be discontinued. They are provided for
            evaluation and should not be relied upon for critical operations.
          </p>

          <h2>17. Suspension and termination</h2>
          <p>You may stop using Boardblend or close your account at any time.</p>
          <p>We may suspend or terminate access if:</p>
          <ul>
            <li>You materially violate these Terms.</li>
            <li>Your use creates a security, legal, or operational risk.</li>
            <li>Payment remains overdue.</li>
            <li>We are required to do so by law.</li>
            <li>We discontinue the service.</li>
          </ul>
          <p>
            Where appropriate, we will give you notice and an opportunity to
            resolve the issue.
          </p>
          <p>
            Following termination, your right to use Boardblend ends.
            Provisions that reasonably need to survive—including ownership,
            payment obligations, disclaimers, liability limitations, and
            dispute terms—will remain in effect.
          </p>

          <h2>18. Disclaimers</h2>
          <p>
            To the maximum extent permitted by law, Boardblend is provided “as
            is” and “as available.”
          </p>
          <p>
            We disclaim warranties of merchantability, fitness for a particular
            purpose, non-infringement, accuracy, availability, and uninterrupted
            operation.
          </p>
          <p>We do not guarantee:</p>
          <ul>
            <li>The accuracy of transcripts or AI-generated content.</li>
            <li>That uploaded content will never be lost.</li>
            <li>
              That Boardblend will meet every technical or business requirement.
            </li>
            <li>The conduct of other session participants.</li>
            <li>The operation of third-party AI providers or integrations.</li>
          </ul>
          <p>
            Nothing in these Terms excludes warranties or protections that
            cannot legally be excluded.
          </p>

          <h2>19. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, Boardblend and its owners,
            employees, affiliates, and service providers will not be liable for
            indirect, incidental, special, consequential, exemplary, or
            punitive damages, including lost profits, lost data, lost business,
            or business interruption.
          </p>
          <p>
            Our total liability arising from or relating to Boardblend will not
            exceed the amount you paid to Boardblend during the last month.
          </p>
          <p>
            These limitations do not apply where liability cannot legally be
            limited, including liability resulting from fraud, wilful
            misconduct, or other exclusions prohibited by applicable law.
          </p>

          <h2>20. Indemnification</h2>
          <p>
            To the extent permitted by law, you agree to indemnify and hold
            Boardblend harmless from third-party claims arising from:
          </p>
          <ul>
            <li>Content you upload or share.</li>
            <li>Your violation of these Terms.</li>
            <li>Your violation of another person&apos;s rights.</li>
            <li>
              Your unlawful use of transcription or connected services.
            </li>
          </ul>
          <p>
            This section does not apply to individual consumers where prohibited
            by applicable law.
          </p>

          <h2>21. Governing law</h2>
          <p>
            These Terms are governed by the laws of the Province of Ontario and
            the federal laws of Canada applicable there, without regard to
            conflict-of-law rules.
          </p>
          <p>
            Subject to any consumer rights that require otherwise, disputes will
            be brought before the courts located in Ontario.
          </p>
          <p>
            Nothing in these Terms prevents you from exercising rights available
            under the mandatory consumer laws of your province, state, or
            country.
          </p>

          <h2>22. Changes to these Terms</h2>
          <p>
            We may update these Terms as Boardblend changes. We will post the
            revised Terms and update the effective date. We will provide
            additional notice before material changes take effect where
            appropriate.
          </p>
          <p>
            Continuing to use Boardblend after updated Terms take effect means
            you accept them. If you do not agree, you must stop using the
            service.
          </p>

          <h2>23. General terms</h2>
          <p>
            If any provision is found unenforceable, the remaining provisions
            will continue in effect.
          </p>
          <p>
            Our failure to enforce a provision is not a waiver of our right to
            enforce it later.
          </p>
          <p>
            You may not transfer these Terms without our consent. We may
            transfer them as part of a merger, acquisition, corporate
            restructuring, or sale of Boardblend.
          </p>
          <p>
            These Terms, together with the Privacy Policy and any terms shown at
            purchase, form the entire agreement between you and Boardblend
            regarding the service.
          </p>
        </article>
        <HomeFooter />
      </main>
    </>
  );
}
